'use client';
import React, { useState, useEffect } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Typography,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Alert,
  FormHelperText,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { AuthEventKeys, AuthEvents } from '../events';
import { Auth as AuthDict } from '@/shared/i18n';
import { EventBus } from '@/shared/utils';
import { Auth } from 'firebase/auth';
import { ChangePasswordFormInputs, changePasswordSchema } from '../types/password';

type NewPasswordProps = {
  eventBus: EventBus<AuthEvents>;
  dict: AuthDict;
  auth: Auth;
};

function NewPassword({ eventBus, dict, auth }: NewPasswordProps) {
  const {
    control,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ChangePasswordFormInputs>({
    resolver: zodResolver(
      changePasswordSchema({
        minLengthMessage: dict.change_password.validation_password_to_short,
        numberRequiredMessage: dict.change_password.validation_number_required,
        passwordsMismatchMessage: dict.change_password.validation_password_mismatch,
      }),
    ),
    mode: 'onChange',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [oobCode, setOobCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('oobCode');
    if (code) {
      setOobCode(code);
    } else {
      setError(dict.auth_errors?.invalid_oob_code || 'Codice di reset non valido.');
    }
  }, [dict]);

  useEffect(() => {
    const handleError = (error: Error) => {
      setError(dict.auth_errors.short_password);
    };
    const handleSuccess = () => {
      setError(null);
    };
    eventBus.on(AuthEventKeys.newPassword.error, handleError);
    eventBus.on(AuthEventKeys.newPassword.success, handleSuccess);
    return () => {
      eventBus.off(AuthEventKeys.newPassword.error, handleError);
      eventBus.off(AuthEventKeys.newPassword.success, handleSuccess);
    };
  }, [eventBus, dict]);

  const onSubmit: SubmitHandler<ChangePasswordFormInputs> = ({ password, confirmPassword }) => {
    if (!oobCode) {
      setError(dict.auth_errors?.invalid_oob_code || 'Codice non valido.');
      return;
    }
    eventBus.emit(AuthEventKeys.newPassword.do, { password, confirmPassword, oobCode, auth });
  };

  return (
    <section className="flex z-10 flex-col self-center w-full max-w-[600px] px-6 py-8 md:px-16 md:py-12 rounded-xl bg-paper">
      <div className="flex flex-col gap-2 text-center">
        <Typography variant="mac-sans-h3" component="h1" color="textPrimary">
          {dict.new_password?.title || 'Resetta password'}
        </Typography>
        <Typography variant="body-500" component="p" color="textSecondary">
          {dict.new_password?.description || 'Inserisci la tua nuova password'}
        </Typography>
      </div>
      <form className="flex flex-col gap-8 mt-12" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-4">
          <FormControl variant="outlined" className="w-full" error={!!errors.password}>
            <InputLabel htmlFor="new-password">
              <Typography variant="s2">
                {dict.new_password?.new_password_label || 'Nuova Password'}
              </Typography>
            </InputLabel>
            <Controller
              name="password"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <OutlinedInput
                  {...field}
                  placeholder="Inserisci nuova password"
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label={
                    <Typography variant="s2">
                      {dict.new_password?.new_password_label || 'Nuova Password'}
                    </Typography>
                  }
                />
              )}
            />
            <FormHelperText>
              <Typography variant="caption-small">{errors.password?.message}</Typography>
            </FormHelperText>
          </FormControl>
          <FormControl variant="outlined" className="w-full" error={!!errors.confirmPassword}>
            <InputLabel htmlFor="confirm-password">
              {dict.new_password?.confirm_password_label || 'Conferma Password'}
            </InputLabel>
            <Controller
              name="confirmPassword"
              control={control}
              defaultValue=""
              render={({ field }) => (
                <OutlinedInput
                  {...field}
                  placeholder="Conferma password"
                  id="confirm-password"
                  type={showPassword ? 'text' : 'password'}
                  error={!!error}
                  endAdornment={
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle confirm password visibility"
                        onClick={handleClickShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  }
                  label={dict.new_password?.confirm_password_label || 'Conferma Password'}
                />
              )}
            />
            <FormHelperText>
              <Typography variant="caption-small">{errors.confirmPassword?.message}</Typography>
            </FormHelperText>
          </FormControl>
        </div>
        {error && (
          <Alert severity="error" className="mt-4">
            {error}
          </Alert>
        )}
        <Button
          type="submit"
          size="large"
          variant="contained"
          color="primary"
          className="w-full mt-4"
          disabled={!isValid}
        >
          {dict.new_password?.button_text || 'Reset Password'}
        </Button>
      </form>
    </section>
  );
}

export default NewPassword;
